import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, RefreshCw, Plus, Trash2, Edit, Save, X, Receipt,
  Zap, Globe, ShoppingBag, Megaphone, MoreHorizontal, Calendar, TrendingDown
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_FULL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CATEGORIES = {
  'site': { label: 'Site/Hospedagem', icon: Globe, color: 'bg-blue-500' },
  'luz': { label: 'Luz/Energia', icon: Zap, color: 'bg-yellow-500' },
  'uniformes': { label: 'Uniformes', icon: ShoppingBag, color: 'bg-purple-500' },
  'marketing': { label: 'Marketing', icon: Megaphone, color: 'bg-pink-500' },
  'escritorio': { label: 'Escritório', icon: Receipt, color: 'bg-gray-500' },
  'outros': { label: 'Outros', icon: MoreHorizontal, color: 'bg-gray-400' }
};

const AdminGeneralExpenses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [activeTab, setActiveTab] = useState('anual');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Modal de exclusão de despesas recorrentes
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteExpense, setDeleteExpense] = useState(null);
  
  const [formData, setFormData] = useState({
    category: 'outros',
    description: '',
    value: 0,
    date: new Date().toISOString().split('T')[0],
    payment_method: 'pix',
    notes: '',
    is_recurring: false,
    is_installment: false,
    installment_months: 1
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadExpenses();
  }, [navigate]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/cash-flow/general-expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Erro ao carregar despesas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const isCurrentDay = (dayIndex) => {
    return selectedYear === currentYear && selectedMonth === currentMonth && (dayIndex + 1) === currentDay;
  };

  const isCurrentMonth = (monthIndex) => {
    return selectedYear === currentYear && monthIndex === currentMonth;
  };

  // Processar despesas por mês e categoria
  const expensesByMonth = useMemo(() => {
    const result = {
      byCategory: {},
      totals: Array(12).fill(0),
      byDay: Array(12).fill(null).map(() => Array(31).fill(0)),
      byCategoryByDay: {}
    };

    Object.keys(CATEGORIES).forEach(cat => {
      result.byCategory[cat] = Array(12).fill(0);
      result.byCategoryByDay[cat] = Array(12).fill(null).map(() => Array(31).fill(0));
    });

    expenses.forEach(exp => {
      if (!exp.date) return;
      const date = new Date(exp.date + 'T12:00:00');
      if (date.getFullYear() !== selectedYear) return;

      const monthIdx = date.getMonth();
      const day = date.getDate() - 1;
      const category = exp.category || 'outros';
      const value = exp.value || 0;

      result.totals[monthIdx] += value;
      result.byDay[monthIdx][day] += value;

      if (result.byCategory[category]) {
        result.byCategory[category][monthIdx] += value;
        result.byCategoryByDay[category][monthIdx][day] += value;
      }
    });

    return result;
  }, [expenses, selectedYear]);

  const totalAnual = expensesByMonth.totals.reduce((a, b) => a + b, 0);

  const resetForm = () => {
    setFormData({
      category: 'outros',
      description: '',
      value: 0,
      date: new Date().toISOString().split('T')[0],
      payment_method: 'pix',
      notes: '',
      is_recurring: false,
      is_installment: false,
      installment_months: 1
    });
    setEditingExpense(null);
  };

  const openModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        category: expense.category,
        description: expense.description,
        value: expense.value,
        date: expense.date,
        payment_method: expense.payment_method || 'pix',
        notes: expense.notes || '',
        is_recurring: false,
        is_installment: false,
        installment_months: 1
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.description || !formData.value) {
        setMessage({ type: 'error', text: 'Preencha descrição e valor.' });
        return;
      }

      if (editingExpense) {
        await axios.put(`${BACKEND_URL}/api/cash-flow/general-expenses/${editingExpense.id}`, {
          category: formData.category,
          description: formData.description,
          value: formData.value,
          date: formData.date,
          payment_method: formData.payment_method,
          notes: formData.notes
        });
        setMessage({ type: 'success', text: 'Despesa atualizada!' });
      } else {
        // Criar despesas (pode ser múltiplas se recorrente ou parcelado)
        const expensesToCreate = [];
        const baseDate = new Date(formData.date + 'T12:00:00');

        if (formData.is_recurring) {
          // Criar para todos os meses do ano
          for (let m = 0; m < 12; m++) {
            const newDate = new Date(baseDate.getFullYear(), m, Math.min(baseDate.getDate(), getDaysInMonth(m, baseDate.getFullYear())));
            expensesToCreate.push({
              ...formData,
              date: newDate.toISOString().split('T')[0],
              description: `${formData.description} (${MONTHS[m]})`
            });
          }
        } else if (formData.is_installment && formData.installment_months > 1) {
          // Criar parcelas
          for (let i = 0; i < formData.installment_months; i++) {
            const newDate = new Date(baseDate);
            newDate.setMonth(newDate.getMonth() + i);
            expensesToCreate.push({
              ...formData,
              date: newDate.toISOString().split('T')[0],
              description: `${formData.description} (${i + 1}/${formData.installment_months})`
            });
          }
        } else {
          expensesToCreate.push(formData);
        }

        // Criar todas as despesas
        for (const exp of expensesToCreate) {
          await axios.post(`${BACKEND_URL}/api/cash-flow/general-expenses`, {
            category: exp.category,
            description: exp.description,
            value: exp.value,
            date: exp.date,
            payment_method: exp.payment_method,
            notes: exp.notes
          });
        }
        setMessage({ type: 'success', text: `${expensesToCreate.length} despesa(s) criada(s)!` });
      }

      setShowModal(false);
      resetForm();
      await loadExpenses();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao salvar despesa.' });
    }
  };

  const handleDelete = async (expense, deleteFuture = false) => {
    try {
      if (deleteFuture) {
        if (expense.group_id) {
          // Tem group_id - deletar por grupo
          await axios.delete(`${BACKEND_URL}/api/cash-flow/general-expenses/${expense.id}?delete_future=true&group_id=${expense.group_id}&date=${expense.date}`);
        } else {
          // Não tem group_id - deletar por padrão de descrição
          const baseDescription = encodeURIComponent(expense.description);
          await axios.delete(`${BACKEND_URL}/api/cash-flow/general-expenses/${expense.id}?delete_future=true&base_description=${baseDescription}&date=${expense.date}`);
        }
        setMessage({ type: 'success', text: 'Despesa e futuras parcelas excluídas!' });
      } else {
        // Deletar apenas esta despesa
        await axios.delete(`${BACKEND_URL}/api/cash-flow/general-expenses/${expense.id}`);
        setMessage({ type: 'success', text: 'Despesa excluída!' });
      }
      setShowDeleteModal(false);
      setDeleteExpense(null);
      await loadExpenses();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao excluir despesa.' });
    }
  };
  
  // Função para detectar se uma despesa parece ser recorrente pelo padrão do nome
  const looksRecurring = (expense) => {
    const monthPatterns = ['(Jan)', '(Fev)', '(Mar)', '(Abr)', '(Mai)', '(Jun)', 
                          '(Jul)', '(Ago)', '(Set)', '(Out)', '(Nov)', '(Dez)',
                          '(Janeiro)', '(Fevereiro)', '(Março)', '(Abril)', '(Maio)', '(Junho)',
                          '(Julho)', '(Agosto)', '(Setembro)', '(Outubro)', '(Novembro)', '(Dezembro)',
                          '1/', '2/', '3/', '4/', '5/', '6/', '7/', '8/', '9/', '10/', '11/', '12/'];
    const desc = expense.description || '';
    return monthPatterns.some(pattern => desc.includes(pattern));
  };
  
  const confirmDelete = (expense) => {
    // Se é recorrente, parcelada, tem group_id, ou parece recorrente pelo nome
    if (expense.is_recurring || expense.is_installment || expense.group_id || looksRecurring(expense)) {
      setDeleteExpense(expense);
      setShowDeleteModal(true);
    } else {
      // Se não é recorrente, confirmar e deletar direto
      if (window.confirm('Excluir esta despesa?')) {
        handleDelete(expense, false);
      }
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const daysInSelectedMonth = getDaysInMonth(selectedMonth, selectedYear);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-full mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <Receipt className="w-6 h-6" />
            <h1 className="text-xl font-bold">Despesas Gerais - {selectedYear}</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadExpenses} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => openModal()} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Plus className="w-4 h-4 mr-2" />
              Nova Despesa
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-full mx-auto px-4 py-6">
        {/* Mensagem */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-4">×</button>
          </div>
        )}

        {/* Card de Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700 font-medium">Total Despesas {selectedYear}</span>
              </div>
              <p className="text-2xl font-bold text-red-700">{formatPrice(totalAnual)}</p>
            </CardContent>
          </Card>
          {Object.entries(CATEGORIES).slice(0, 3).map(([key, { label, color }]) => (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-3 h-3 rounded-full ${color}`}></div>
                  <span className="text-sm text-gray-600 font-medium">{label}</span>
                </div>
                <p className="text-xl font-bold text-gray-800">
                  {formatPrice(expensesByMonth.byCategory[key]?.reduce((a, b) => a + b, 0) || 0)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs Anual / Mensal */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="anual">📅 Visão Anual</TabsTrigger>
              <TabsTrigger value="mensal">📆 Visão Mensal</TabsTrigger>
            </TabsList>

            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeTab === 'mensal' && (
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_FULL.map((m, i) => (
                    <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* VISÃO ANUAL */}
          <TabsContent value="anual">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Despesas Mensais - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#003366] text-white">
                        <th className="p-3 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[180px]">Categoria</th>
                        {MONTHS.map((m, idx) => (
                          <th key={idx} className={`p-2 text-center font-semibold min-w-[90px] ${isCurrentMonth(idx) ? 'bg-yellow-400 text-gray-900' : ''}`}>
                            {m}
                          </th>
                        ))}
                        <th className="p-2 text-center font-semibold min-w-[100px] bg-[#002244]">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Total Geral */}
                      <tr className="bg-red-100 font-bold">
                        <td className="p-2 sticky left-0 bg-red-100 z-10">💸 TOTAL DESPESAS</td>
                        {expensesByMonth.totals.map((val, idx) => (
                          <td key={idx} className="p-2 text-right text-red-700">
                            {val === 0 ? '-' : formatPrice(val)}
                          </td>
                        ))}
                        <td className="p-2 text-right text-red-700 bg-red-200">{formatPrice(totalAnual)}</td>
                      </tr>

                      {/* Por Categoria */}
                      {Object.entries(CATEGORIES).map(([key, { label, icon: Icon, color }]) => {
                        const catTotal = expensesByMonth.byCategory[key]?.reduce((a, b) => a + b, 0) || 0;
                        if (catTotal === 0) return null;
                        return (
                          <tr key={key} className="border-b hover:bg-gray-50">
                            <td className="p-2 sticky left-0 bg-white z-10">
                              <div className="flex items-center gap-2 pl-4">
                                <div className={`w-3 h-3 rounded-full ${color}`}></div>
                                {label}
                              </div>
                            </td>
                            {expensesByMonth.byCategory[key]?.map((val, idx) => (
                              <td key={idx} className="p-2 text-right text-gray-700">
                                {val === 0 ? '-' : formatPrice(val)}
                              </td>
                            ))}
                            <td className="p-2 text-right font-medium bg-gray-100">{formatPrice(catTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VISÃO MENSAL */}
          <TabsContent value="mensal">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Despesas Diárias - {MONTHS_FULL[selectedMonth]} {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Tabela Dias 1-16 */}
                <div className="overflow-x-auto mb-4">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-[#003366] text-white">
                        <th className="p-2 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[140px]">Categoria</th>
                        {Array(16).fill(0).map((_, i) => (
                          <th key={i} className={`p-1 text-center font-semibold min-w-[55px] ${
                            i + 1 > daysInSelectedMonth ? 'bg-gray-800' : isCurrentDay(i) ? 'bg-yellow-400 text-gray-900' : ''
                          }`}>
                            {i + 1 <= daysInSelectedMonth ? i + 1 : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-red-100 font-bold">
                        <td className="p-1 sticky left-0 bg-red-100 z-10">💸 TOTAL</td>
                        {Array(16).fill(0).map((_, i) => (
                          <td key={i} className={`p-1 text-right text-red-700 ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                            {i + 1 <= daysInSelectedMonth ? (expensesByMonth.byDay[selectedMonth][i] > 0 ? formatPrice(expensesByMonth.byDay[selectedMonth][i]) : '-') : ''}
                          </td>
                        ))}
                      </tr>
                      {Object.entries(CATEGORIES).map(([key, { label, color }]) => {
                        const hasValues = expensesByMonth.byCategoryByDay[key]?.[selectedMonth]?.some(v => v > 0);
                        if (!hasValues) return null;
                        return (
                          <tr key={key} className="border-b hover:bg-gray-50">
                            <td className="p-1 sticky left-0 bg-white z-10 pl-4">
                              <div className="flex items-center gap-1">
                                <div className={`w-2 h-2 rounded-full ${color}`}></div>
                                <span className="text-xs">{label}</span>
                              </div>
                            </td>
                            {Array(16).fill(0).map((_, i) => (
                              <td key={i} className={`p-1 text-right text-gray-600 ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                                {i + 1 <= daysInSelectedMonth ? (expensesByMonth.byCategoryByDay[key][selectedMonth][i] > 0 ? formatPrice(expensesByMonth.byCategoryByDay[key][selectedMonth][i]) : '-') : ''}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Tabela Dias 17-31 */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-[#003366] text-white">
                        <th className="p-2 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[140px]">Categoria</th>
                        {Array(15).fill(0).map((_, i) => (
                          <th key={i} className={`p-1 text-center font-semibold min-w-[55px] ${
                            i + 17 > daysInSelectedMonth ? 'bg-gray-800' : isCurrentDay(i + 16) ? 'bg-yellow-400 text-gray-900' : ''
                          }`}>
                            {i + 17 <= daysInSelectedMonth ? i + 17 : ''}
                          </th>
                        ))}
                        <th className="p-2 text-center font-semibold min-w-[80px] bg-[#002244]">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-red-100 font-bold">
                        <td className="p-1 sticky left-0 bg-red-100 z-10">💸 TOTAL</td>
                        {Array(15).fill(0).map((_, i) => (
                          <td key={i} className={`p-1 text-right text-red-700 ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                            {i + 17 <= daysInSelectedMonth ? (expensesByMonth.byDay[selectedMonth][i + 16] > 0 ? formatPrice(expensesByMonth.byDay[selectedMonth][i + 16]) : '-') : ''}
                          </td>
                        ))}
                        <td className="p-1 text-right text-red-700 bg-red-200 font-bold">
                          {formatPrice(expensesByMonth.totals[selectedMonth])}
                        </td>
                      </tr>
                      {Object.entries(CATEGORIES).map(([key, { label, color }]) => {
                        const hasValues = expensesByMonth.byCategoryByDay[key]?.[selectedMonth]?.some(v => v > 0);
                        if (!hasValues) return null;
                        const catTotal = expensesByMonth.byCategory[key]?.[selectedMonth] || 0;
                        return (
                          <tr key={key} className="border-b hover:bg-gray-50">
                            <td className="p-1 sticky left-0 bg-white z-10 pl-4">
                              <div className="flex items-center gap-1">
                                <div className={`w-2 h-2 rounded-full ${color}`}></div>
                                <span className="text-xs">{label}</span>
                              </div>
                            </td>
                            {Array(15).fill(0).map((_, i) => (
                              <td key={i} className={`p-1 text-right text-gray-600 ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                                {i + 17 <= daysInSelectedMonth ? (expensesByMonth.byCategoryByDay[key][selectedMonth][i + 16] > 0 ? formatPrice(expensesByMonth.byCategoryByDay[key][selectedMonth][i + 16]) : '-') : ''}
                              </td>
                            ))}
                            <td className="p-1 text-right font-medium bg-gray-100">{formatPrice(catTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Lista de despesas do mês */}
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Detalhamento - {MONTHS_FULL[selectedMonth]}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {expenses
                    .filter(e => {
                      if (!e.date) return false;
                      const d = new Date(e.date + 'T12:00:00');
                      return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
                    })
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(exp => {
                      const cat = CATEGORIES[exp.category] || CATEGORIES.outros;
                      return (
                        <div key={exp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`expense-row-${exp.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${cat.color}`}></div>
                            <div>
                              <p className="font-medium">{exp.description}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(exp.date + 'T12:00:00').toLocaleDateString('pt-BR')} • {cat.label} • {exp.payment_method?.toUpperCase()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-red-600">{formatPrice(exp.value)}</span>
                            <Button size="sm" variant="ghost" onClick={() => openModal(exp)} data-testid={`edit-expense-${exp.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => confirmDelete(exp)} data-testid={`delete-expense-${exp.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  {expenses.filter(e => {
                    if (!e.date) return false;
                    const d = new Date(e.date + 'T12:00:00');
                    return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
                  }).length === 0 && (
                    <p className="text-center text-gray-500 py-8">Nenhuma despesa neste mês.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Criação/Edição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? '✏️ Editar Despesa' : '💸 Nova Despesa Geral'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>Descrição *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Mensalidade Hostinger"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Forma de Pagamento</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_method: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pix">💸 PIX</SelectItem>
                    <SelectItem value="cartao">💳 Cartão</SelectItem>
                    <SelectItem value="boleto">📄 Boleto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!editingExpense && (
              <>
                <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
                  <Switch
                    checked={formData.is_recurring}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_recurring: v, is_installment: false }))}
                  />
                  <div>
                    <Label className="font-medium">🔄 Despesa Recorrente</Label>
                    <p className="text-xs text-gray-500">Cria automaticamente para todos os 12 meses do ano</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg">
                  <Switch
                    checked={formData.is_installment}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_installment: v, is_recurring: false }))}
                  />
                  <div className="flex-1">
                    <Label className="font-medium">📅 Despesa Parcelada</Label>
                    <p className="text-xs text-gray-500">Divide o valor em parcelas mensais</p>
                  </div>
                  {formData.is_installment && (
                    <Input
                      type="number"
                      min="2"
                      max="24"
                      className="w-20"
                      value={formData.installment_months}
                      onChange={(e) => setFormData(prev => ({ ...prev, installment_months: parseInt(e.target.value) || 2 }))}
                      placeholder="Meses"
                    />
                  )}
                </div>
              </>
            )}

            <div>
              <Label>Observações</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Observações opcionais"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={handleSave} className="flex-1 bg-[#003366]">
                <Save className="w-4 h-4 mr-2" />
                {editingExpense ? 'Atualizar' : 'Criar Despesa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Exclusão de Despesa Recorrente */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>🗑️ Excluir Despesa Recorrente</DialogTitle>
          </DialogHeader>
          {deleteExpense && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{deleteExpense.description}</p>
                <p className="text-sm text-gray-500">
                  {formatPrice(deleteExpense.value)} - {new Date(deleteExpense.date + 'T12:00:00').toLocaleDateString('pt-BR')}
                </p>
              </div>
              
              <p className="text-sm text-gray-600">
                Esta despesa faz parte de uma série recorrente. O que deseja fazer?
              </p>
              
              <div className="flex flex-col gap-2">
                <Button 
                  variant="outline"
                  onClick={() => handleDelete(deleteExpense, false)}
                  className="w-full"
                >
                  Excluir apenas esta
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleDelete(deleteExpense, true)}
                  className="w-full"
                >
                  Excluir esta e futuras parcelas
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => { setShowDeleteModal(false); setDeleteExpense(null); }}
                  className="w-full"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGeneralExpenses;
